import React, { useEffect, useState } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { GestureDetector, Gesture } from 'react-native-gesture-handler'
import Animated, {
    useSharedValue,
    useAnimatedStyle,
    withTiming,
    runOnJS,
} from 'react-native-reanimated'

// Singleton para manejar el overlay global
let globalOverlayCallback = null
let globalZoomedImageCallback = null

export const setGlobalOverlay = (callback) => {
    globalOverlayCallback = callback
}

export const setGlobalZoomedImage = (callback) => {
    globalZoomedImageCallback = callback
}

const updateGlobalOverlay = (opacity) => {
    if (globalOverlayCallback) {
        globalOverlayCallback(opacity)
    }
}

const updateGlobalZoomedImage = (data) => {
    if (globalZoomedImageCallback) {
        globalZoomedImageCallback(data)
    }
}

/**
 * Componente wrapper que agrega pinch-to-zoom a su children
 * Solo se activa en mobile (iOS/Android), en web actúa como un View normal
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Contenido a envolver (típicamente una imagen)
 * @param {number} [props.maxScale=4] - Escala máxima permitida
 * @param {number} [props.minScale=1] - Escala mínima permitida
 * @param {boolean} [props.enabled=true] - Habilitar/deshabilitar el gesto
 * @param {Function} [props.onDoubleTap] - Callback para doble tap (like/reacción)
 * @param {Function} [props.onLongPress] - Callback para long press (selector de reacciones)
 * @param {Function} [props.onZoomChange] - Callback cuando cambia el estado de zoom (true/false)
 */
const PinchZoomImage = ({ 
    children, 
    maxScale = 4, 
    minScale = 1,
    enabled = true,
    onDoubleTap,
    onLongPress,
    onZoomChange
}) => {
    // En web, simplemente retornar el children sin gestos
    if (Platform.OS === 'web') {
        return children
    }

    // Solo aplicar gestos en mobile si está habilitado
    if (!enabled) {
        return children
    }

    const scale = useSharedValue(1)
    const savedScale = useSharedValue(1)
    const translateX = useSharedValue(0)
    const translateY = useSharedValue(0)
    const savedTranslateX = useSharedValue(0)
    const savedTranslateY = useSharedValue(0)
    const isZooming = useSharedValue(0) // 0 = no zoom, 1 = zooming
    const elevation = useSharedValue(0) // Elevación dinámica para Android
    const overlayOpacity = useSharedValue(0) // Opacidad del overlay negro
    const [childrenClone, setChildrenClone] = useState(null)

    // Sincronizar overlay con el componente global
    useEffect(() => {
        return () => {
            // Limpiar overlay al desmontar
            updateGlobalOverlay(0)
            updateGlobalZoomedImage(null)
        }
    }, [])

    // Gesto de pan (arrastre) - solo activo cuando hay zoom
    const panGesture = Gesture.Pan()
        .enabled(true)
        .minPointers(2) // Requiere dos dedos (los mismos que el pinch)
        .onStart(() => {
            // Guardar posición actual
            savedTranslateX.value = translateX.value
            savedTranslateY.value = translateY.value
        })
        .onUpdate((event) => {
            // Solo permitir pan si estamos en zoom (escala > 1.1)
            if (scale.value > 1.1) {
                translateX.value = savedTranslateX.value + event.translationX
                translateY.value = savedTranslateY.value + event.translationY
            }
        })
        .runOnJS(true)

    // Gesto de pinch (pellizcar)
    const pinchGesture = Gesture.Pinch()
        .onStart(() => {
            savedScale.value = scale.value
            isZooming.value = 1
            elevation.value = 999
            overlayOpacity.value = 0.85
            runOnJS(updateGlobalOverlay)(0.85)
            // Clonar children para mostrar en el overlay global
            runOnJS(setChildrenClone)(children)
            runOnJS(updateGlobalZoomedImage)({
                children,
                scale: savedScale.value,
                translateX: translateX.value,
                translateY: translateY.value,
            })
            if (typeof onZoomChange === 'function') {
                runOnJS(onZoomChange)(true)
            }
        })
        .onUpdate((event) => {
            const newScale = Math.max(
                minScale,
                Math.min(maxScale, savedScale.value * event.scale)
            )
            scale.value = newScale
            
            const isActive = newScale > 1.1 ? 1 : 0
            isZooming.value = isActive
            elevation.value = isActive > 0 ? 999 : 0
            const targetOpacity = isActive > 0 ? 0.85 : 0
            overlayOpacity.value = targetOpacity
            runOnJS(updateGlobalOverlay)(targetOpacity)
            // Actualizar escala en el componente global
            runOnJS(updateGlobalZoomedImage)({
                children: childrenClone,
                scale: newScale,
                translateX: translateX.value,
                translateY: translateY.value,
            })
        })
        .onEnd(() => {
            savedScale.value = scale.value
            
            scale.value = withTiming(minScale, { duration: 200 })
            translateX.value = withTiming(0, { duration: 200 })
            translateY.value = withTiming(0, { duration: 200 })
            savedScale.value = minScale
            savedTranslateX.value = 0
            savedTranslateY.value = 0
            
            isZooming.value = withTiming(0, { duration: 200 })
            elevation.value = withTiming(0, { duration: 200 })
            overlayOpacity.value = 0
            runOnJS(updateGlobalOverlay)(0)
            runOnJS(updateGlobalZoomedImage)(null)
            runOnJS(setChildrenClone)(null)
            if (typeof onZoomChange === 'function') {
                runOnJS(onZoomChange)(false)
            }
        })
        .runOnJS(true)
        

    // Gesto de doble tap para dar like/reacción
    const doubleTapGesture = Gesture.Tap()
        .numberOfTaps(2)
        .maxDuration(250)
        .onEnd(() => {
            'worklet'
            // console.log('👆👆 [DOUBLE TAP] Triggering like')
            if (typeof onDoubleTap === 'function') {
                onDoubleTap()
            }
        })
        .runOnJS(true)

    // Gesto de long press para selector de reacciones
    const longPressGesture = Gesture.LongPress()
        .minDuration(650)
        .onEnd(() => {
            'worklet'
            // console.log('👇 [LONG PRESS] Opening reactions selector')
            if (typeof onLongPress === 'function') {
                onLongPress()
            }
        })
        .runOnJS(true)

    // Combinar gestos de forma que no se bloqueen entre sí
    // Simultaneous permite que pinch y pan funcionen juntos
    const zoomAndPanGesture = Gesture.Simultaneous(
        pinchGesture,
        panGesture
    )
    
    // Race para que tap gestures tengan prioridad sobre zoom
    const composedGesture = Gesture.Race(
        longPressGesture,
        doubleTapGesture,
        zoomAndPanGesture
    )

    const animatedStyle = useAnimatedStyle(() => {
        try {
            const isActive = isZooming.value > 0
            return {
                transform: [
                    { translateX: translateX.value },
                    { translateY: translateY.value },
                    { scale: scale.value },
                ],
                // Ocultar la imagen original cuando está en zoom (se muestra en el overlay global)
                opacity: isActive ? 0 : 1,
            }
        } catch (error) {
            console.error('❌ [ANIMATED STYLE] Error:', error)
            return {
                transform: [
                    { translateX: 0 },
                    { translateY: 0 },
                    { scale: 1 },
                ],
                opacity: 1,
            }
        }
    })

    return (
        <GestureDetector gesture={composedGesture}>
            <Animated.View style={[styles.container, animatedStyle]}>
                {children}
            </Animated.View>
        </GestureDetector>
    )
}

const styles = StyleSheet.create({
    container: {
        width: '100%',
        alignSelf: 'flex-start',
        overflow: 'visible', // Permitir que el contenido sobresalga
    },
})

// Componente global de overlay que se monta en App.tsx
export const GlobalImageOverlay = () => {
    const [opacity, setOpacity] = React.useState(0)
    const [zoomedImageData, setZoomedImageData] = React.useState(null)

    React.useEffect(() => {
        setGlobalOverlay(setOpacity)
        setGlobalZoomedImage(setZoomedImageData)
        return () => {
            setGlobalOverlay(null)
            setGlobalZoomedImage(null)
        }
    }, [])

    return (
        <>
            {/* Overlay negro */}
            {opacity > 0 && (
                <View 
                    style={[
                        StyleSheet.absoluteFillObject,
                        {
                            backgroundColor: '#000',
                            opacity: opacity,
                            zIndex: 99998,
                            ...(Platform.OS === 'android' && {
                                elevation: 998,
                            }),
                            pointerEvents: 'none',
                        }
                    ]}
                />
            )}
            
            {/* Imagen zoomeada por encima del overlay */}
            {zoomedImageData && (
                <View
                    style={[
                        StyleSheet.absoluteFillObject,
                        {
                            zIndex: 99999,
                            ...(Platform.OS === 'android' && {
                                elevation: 999,
                            }),
                            justifyContent: 'center',
                            alignItems: 'center',
                            pointerEvents: 'box-none',
                        }
                    ]}
                >
                    <Animated.View
                        style={{
                            transform: [
                                { translateX: zoomedImageData.translateX || 0 },
                                { translateY: zoomedImageData.translateY || 0 },
                                { scale: zoomedImageData.scale || 1 },
                            ],
                        }}
                    >
                        {zoomedImageData.children}
                    </Animated.View>
                </View>
            )}
        </>
    )
}

export default PinchZoomImage
